import {useRequiredConfig} from '@domain/shifthandover'
import {FlexPage} from '@hconnect/common/components/FlexPage'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {FilterContrastProvider} from '@hconnect/common/styles'
import {useIsMobile} from '@hconnect/uikit/src/lib2'
import {Grid} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {getFilterDifference, Page, FilterOptions} from '@widgets/shift-event-filters'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {PerformanceFilterBar} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {PerformanceDataView} from '../containers/PerformanceDataView'
import {usePerformancePageFilters, useTrackPerformanceAnalytics} from '../hooks'

type Props = {
  plantId: string
  // Injected per app (cockpit legacy dashboard URL vs Suite buildFeatureUrl) — principle 5.
  dashboardUrl: string
  /**
   * Set to `true` when rendered on the legacy blue surface (Cockpit) so the
   * header controls use the on-blue contrast colours. Suite renders on the
   * light/dark page surface and keeps the default (`false`).
   */
  inverted?: boolean
}

export const PerformancePage: React.FC<Props> = ({plantId, dashboardUrl, inverted = false}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [isMobileFilterOpen, openMobileFilter] = useState(false)
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  useCancelInactiveQueries(queryClient)
  const {country} = useRequiredConfig(plantId)
  const trackAnalyticsEvent = useTrackPerformanceAnalytics({plantId, country})

  const [filterOptions, setFilters] = usePerformancePageFilters(plantId)

  return (
    <FlexPage
      appName="Cockpit"
      title={isMobileFilterOpen ? t('shiftEvent.action.filter') : t('performanceData.pageName')}
      sxActionContent={isMobileFilterOpen ? {width: '100%'} : undefined}
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Grid container alignItems="center">
            <Grid item xs={isMobileFilterOpen ? 12 : undefined}>
              <PerformanceFilterBar
                plantId={plantId}
                activeSettings={filterOptions}
                isCollapsible={isMobile}
                onChange={(nextFilterOptions: FilterOptions) => {
                  setFilters(nextFilterOptions)
                  trackAnalyticsEvent('userAdjustsFilter', {
                    filterPage: Page.Performance,
                    filterName: getFilterDifference(nextFilterOptions, filterOptions)
                  })
                }}
                openMobileFilter={openMobileFilter}
                isMobileFilterOpen={isMobileFilterOpen}
              />
            </Grid>
          </Grid>
        </FilterContrastProvider>
      }
    >
      {!isMobileFilterOpen && <PerformanceDataView plantId={plantId} dashboardUrl={dashboardUrl} />}
    </FlexPage>
  )
}
